import { getBrowserClient } from '@/lib/integrations/supabase/client';
import type { Service, SalesRule } from '@/lib/integrations/supabase/types';

export async function getServices(businessId: string): Promise<Service[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('business_id', businessId)
    .eq('active', true)
    .order('name', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
}

export async function getAllServices(businessId: string): Promise<Service[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('business_id', businessId)
    .order('name', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
}

export async function getService(id: string): Promise<Service | null> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

/**
 * Get effective price for a service (considering active sales).
 */
export async function getServiceEffectivePrice(
  serviceId: string,
  businessId: string
): Promise<number> {
  const supabase = getBrowserClient();
  const now = new Date().toISOString();

  type ServicePrice = { default_price_aed: number };

  const { data: serviceData } = await supabase
    .from('services')
    .select('default_price_aed')
    .eq('id', serviceId)
    .single();

  const service = serviceData as ServicePrice | null;

  if (!service) return 0;

  const { data: salesData } = await supabase
    .from('sales_rules')
    .select('*')
    .eq('business_id', businessId)
    .eq('active', true)
    .lte('starts_at', now)
    .gte('ends_at', now);

  const sales = (salesData ?? []) as SalesRule[];

  const applicableSale = sales.find(sale => {
    const appliesTo = sale.applies_to as string[];
    return appliesTo.includes(serviceId);
  });

  if (applicableSale) {
    if (applicableSale.type === 'fixed') {
      return Number(applicableSale.value);
    } else {
      return Number(service.default_price_aed) * (1 - Number(applicableSale.value) / 100);
    }
  }

  return Number(service.default_price_aed);
}
